<script setup lang="ts">
import { ArrowRight } from "lucide-vue-next";
import PublicPageLayout from "~/components/public/PublicPageLayout.vue";

const { t } = useI18n();
const localeRoute = useLocaleRoute();

const { canonicalUrl, currentLocale } = useLocalizedSeoLinks("/about");

useSeoMeta({
  title: () => t("public_pages.about.seo.title"),
  description: () => t("public_pages.about.seo.description"),
  robots: "index, follow",
  ogTitle: () => t("public_pages.about.seo.title"),
  ogDescription: () => t("public_pages.about.seo.description"),
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});

const principles = ["simple", "direct", "local"] as const;
</script>

<template>
  <PublicPageLayout
    :eyebrow="$t('public_pages.common.about_senta')"
    :title="$t('public_pages.about.title')"
    :description="$t('public_pages.about.description')"
  >
    <div class="max-w-[1180px]">
      <section class="grid gap-10 md:grid-cols-2 md:gap-20">
        <div class="max-w-md">
          <p class="text-xs font-bold uppercase tracking-[0.13em] text-primary">
            {{ $t("public_pages.about.clients.label") }}
          </p>
          <h2 class="mt-3 text-2xl font-bold tracking-tight text-app-text">
            {{ $t("public_pages.about.clients.title") }}
          </h2>
          <p class="mt-4 leading-7 text-app-muted">
            {{ $t("public_pages.about.clients.description") }}
          </p>
          <p class="mt-3 text-sm leading-6 text-app-muted">
            {{ $t("public_pages.about.clients.extra") }}
          </p>
        </div>

        <div class="max-w-md">
          <p class="text-xs font-bold uppercase tracking-[0.13em] text-primary">
            {{ $t("public_pages.about.professionals.label") }}
          </p>
          <h2 class="mt-3 text-2xl font-bold tracking-tight text-app-text">
            {{ $t("public_pages.about.professionals.title") }}
          </h2>
          <p class="mt-4 leading-7 text-app-muted">
            {{ $t("public_pages.about.professionals.description") }}
          </p>
          <p class="mt-3 text-sm leading-6 text-app-muted">
            {{ $t("public_pages.about.professionals.extra") }}
          </p>
        </div>
      </section>

      <section class="mt-20 sm:mt-24">
        <div class="max-w-3xl">
          <p class="text-xs font-bold uppercase tracking-[0.13em] text-primary">
            {{ $t("public_pages.about.direction.label") }}
          </p>
          <h2 class="mt-3 text-3xl font-bold tracking-tight text-app-text">
            {{ $t("public_pages.about.principles.title") }}
          </h2>
          <p class="mt-4 max-w-2xl leading-7 text-app-muted">
            {{ $t("public_pages.about.direction.first") }}
          </p>
        </div>

        <div class="mt-10 grid gap-10 sm:grid-cols-3 sm:gap-14">
          <div v-for="(key, index) in principles" :key="key" class="max-w-xs">
            <span class="text-xs font-bold tabular-nums text-primary"
              >0{{ index + 1 }}</span
            >
            <h3 class="mt-3 text-lg font-bold text-app-text">
              {{ $t(`public_pages.about.principles.${key}.title`) }}
            </h3>
            <p class="mt-2 text-sm leading-6 text-app-muted">
              {{ $t(`public_pages.about.principles.${key}.description`) }}
            </p>
          </div>
        </div>
      </section>

      <section
        class="mt-20 flex flex-col gap-5 sm:mt-24 sm:flex-row sm:items-end sm:justify-between"
      >
        <div class="max-w-xl">
          <h2 class="text-xl font-bold text-app-text">
            {{ $t("public_pages.about.next.title") }}
          </h2>
          <p class="mt-2 text-sm leading-6 text-app-muted">
            {{ $t("public_pages.about.next.description") }}
          </p>
        </div>
        <div class="flex flex-wrap gap-x-7 gap-y-3">
          <NuxtLink
            :to="localeRoute('/how-it-works') || '/how-it-works'"
            class="inline-flex items-center gap-2 text-sm font-bold text-primary animated-underline"
          >
            {{ $t("public_pages.about.next.how_it_works") }}
            <ArrowRight class="size-4" />
          </NuxtLink>
          <NuxtLink
            :to="localeRoute('/for-professionals') || '/for-professionals'"
            class="inline-flex items-center gap-2 text-sm font-bold text-primary animated-underline"
          >
            {{ $t("public_pages.about.next.for_professionals") }}
            <ArrowRight class="size-4" />
          </NuxtLink>
        </div>
      </section>
    </div>
  </PublicPageLayout>
</template>
